import SearchWithFilters from "@widgets/SearchWithFilters";
import { Button, Divider, Flex } from "antd";
import { format } from "date-fns";
import dayjs from "dayjs";
import { Search, SlidersHorizontal } from "lucide-react";
import React, { useMemo } from "react";
import styles from "./InfluencerSearchHeader.module.scss";

// Imports for filters configuration, store, and same UI components
import { useGetInfluencerStore } from "@entities/discovery/lib/store/useGetInfluencerStore";
import { MultiListFilter } from "@entities/global-discovery/ui/Filters/MultiListFilter";
import { RangeFilter } from "@entities/global-discovery/ui/Filters/RangeFilter";
import { IGFilters, OtherFilters, YtFilters } from "@shared/lib/types";

type InfluencerSearchHeaderProps = {
  title: string;
  isFromBucketPage?: boolean;
  feUsername: string;
  setFeUserName: (value: string) => void;
  platform: string;
  setPlatform: (platform: string) => void;
  platformsToDisplay: { value: string; label: string }[];
  getFacetTitle: (key: string) => string;
  showAdvanced: boolean;
  onToggleFilters: () => void;
  onClearFilters: () => void;
  onRemoveFilter: (key: string) => void;
  onSearchClick: () => void;
  TopEditButton: () => React.ReactNode;
};

// Formatting logic identical to SelectedFilters
const getFilterValueString = (filterValue: any): string | null => {
  switch (typeof filterValue) {
    case "string":
      return filterValue.trim() ? filterValue : null;
    case "number":
      return `> ${filterValue}`;
    case "boolean":
      return filterValue ? "True" : "False";
    case "object":
      if (!filterValue || Object.keys(filterValue).length === 0) return null;
      if (Array.isArray(filterValue)) {
        if (filterValue.length === 0) return null;
        if (filterValue.every((item) => typeof item === "string")) {
          return filterValue.join(", ");
        } else if (
          filterValue.every(
            (item) => typeof item === "object" && "percentage" in item
          )
        ) {
          return filterValue
            .map((item) => `${item?.name || item?.value}:${item.percentage}%`)
            .join(", ");
        } else {
          return null;
        }
      } else if ("min" in filterValue || "max" in filterValue) {
        if (filterValue?.min && filterValue?.max) {
          return `>=${filterValue.min} && <=${filterValue.max}`;
        }
        if (filterValue?.min) {
          return `>=${filterValue?.min} `;
        }
        return `<=${filterValue?.max} `;
      } else if ("from" in filterValue && "to" in filterValue) {
        if (
          filterValue?.from &&
          filterValue?.to &&
          typeof filterValue?.from === "string" &&
          typeof filterValue?.to === "string" &&
          dayjs(filterValue?.from).isValid() &&
          dayjs(filterValue?.to).isValid()
        ) {
          return `${format(
            new Date(filterValue?.from).toISOString(),
            "dd MMM yyyy"
          )} - ${format(
            new Date(filterValue?.to).toISOString(),
            "dd MMM yyyy"
          )}`;
        } else if (filterValue?.from && filterValue?.to) {
          return `${filterValue?.from} - ${filterValue?.to}`;
        }
        return null;
      } else if ("percentage" in filterValue) {
        return `${filterValue.name}:${filterValue.percentage}`;
      } else {
        return null;
      }
    default:
      return null;
  }
};

export function InfluencerSearchHeader({
  title,
  isFromBucketPage = false,
  feUsername,
  setFeUserName,
  platform,
  setPlatform,
  platformsToDisplay,
  getFacetTitle,
  showAdvanced,
  onToggleFilters,
  onClearFilters,
  onRemoveFilter,
  onSearchClick,
  TopEditButton,
}: InfluencerSearchHeaderProps) {
  // Read store states
  const { selectedFilters, updateFilters, facets } = useGetInfluencerStore();

  // Custom setSelectedFilter bridge to synchronize updates to useGetInfluencerStore
  const handleSetSelectedFilter = (updateFn: (prev: any) => any) => {
    const nextFilters = updateFn(selectedFilters);
    
    // Update modified or new keys
    Object.entries(nextFilters).forEach(([key, value]) => {
      if (key === "filterTags") return;
      if (JSON.stringify(selectedFilters[key]) !== JSON.stringify(value)) {
        updateFilters(key, value as any);
      }
    });

    // Remove deleted keys
    Object.keys(selectedFilters).forEach((key) => {
      if (nextFilters[key] === undefined) {
        updateFilters(key, undefined);
      }
    });
  };

  // Resolve filters configurations based on platform, mapping them to expectations of the reused components
  const followersFilterConfig = useMemo(() => {
    const filters =
      platform === "instagram"
        ? IGFilters
        : platform === "youtube"
        ? YtFilters
        : OtherFilters;
    const facetName = "follower";
    const f = (filters as any[]).find((f) => f.facetName === facetName);
    if (f) {
      return {
        ...f,
        compType: "range",
        optionsList: f.optionsList || [
          { label: "Nano (1k - 10k)", value: { min: 1000, max: 10000 } },
          { label: "Micro (10k - 50k)", value: { min: 10000, max: 50000 } },
          { label: "Mid-Tier (50k - 100k)", value: { min: 50000, max: 100000 } },
          { label: "Macro (100k - 500k)", value: { min: 100000, max: 500000 } },
          { label: "Mega (500k - 1M)", value: { min: 500000, max: 1000000 } },
          { label: "Celebrity (1M+)", value: { min: 1000000 } },
        ],
      };
    }
    return undefined;
  }, [platform]);

  const locationFilterConfig = useMemo(() => {
    const filters =
      platform === "instagram"
        ? IGFilters
        : platform === "youtube"
        ? YtFilters
        : OtherFilters;
    const facetName = "location";
    const f = (filters as any[]).find((f) => f.facetName === facetName);
    if (f) {
      return {
        ...f,
        compType: "multiList",
      };
    }
    return undefined;
  }, [platform]);

  // Map location facets to the layout options format of SelectButton
  const locationOptions = useMemo(() => {
    const locationData = facets?.creator_locations || facets?.location || [];
    return locationData.map((item: any) => ({
      label: item._id,
      value: item._id,
    }));
  }, [facets]);

  // Count active filters in selectedFilters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    Object.values(selectedFilters || {}).forEach((v) => {
      if (v !== undefined && v !== null) {
        if (Array.isArray(v)) {
          if (v.length > 0) count++;
        } else if (typeof v === "object") {
          if (Object.keys(v).length > 0) count++;
        } else {
          count++;
        }
      }
    });
    return count;
  }, [selectedFilters]);

  // Generate chips from selectedFilters
  const chips = useMemo(() => {
    const items: { key: string; label: string }[] = [];
    Object.entries(selectedFilters || {}).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      const facetTitle = getFacetTitle(key) || key;
      const formattedValue = getFilterValueString(value);
      if (formattedValue) {
        items.push({
          key,
          label: `${facetTitle}: ${formattedValue}`,
        });
      }
    });
    return items;
  }, [selectedFilters, getFacetTitle]);

  return (
    <div className={styles.container}>
      {/* Header Banner Section */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerIcon}>
            <Search size={24} />
          </div>
          <div>
            <h2 className={styles.headerTitle}>{title}</h2>
            <p className={styles.headerSubtitle}>
              {isFromBucketPage
                ? "Search and filter profiles in this bucket."
                : "Search and filter onboarded creator profiles."}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={styles.content}>
        {/* Row 1: Search Inputs and Edit Actions */}
        <div className={styles.searchRow}>
          <SearchWithFilters
            inputProps={{
              placeholder: "Search by username, Name or Link",
              prefix: <Search size={14} />,
              value: feUsername,
              onChange: (e) => setFeUserName(e.target.value),
              allowClear: true,
              style: { width: 450, maxWidth: "100%" },
            }}
            selectProps={[
              {
                value: platform,
                onChange: (value) => setPlatform(value as any),
                options: platformsToDisplay,
                placeholder: "Select Platform",
              },
            ]}
          />
          {TopEditButton()}
        </div>

        <Divider className={styles.divider} />

        {/* Row 2: Reused RangeFilter & MultiListFilter and Advanced Sidebar Toggle */}
        <Flex gap={16} align="center">
          {followersFilterConfig && (
            <RangeFilter
              filter={followersFilterConfig as any}
              showTitle={false}
              selectedFilters={selectedFilters}
              setSelectedFilter={handleSetSelectedFilter}
            />
          )}

          {locationFilterConfig && (
            <MultiListFilter
              filter={locationFilterConfig as any}
              showTitle={false}
              selectedFilters={selectedFilters}
              setSelectedFilter={handleSetSelectedFilter}
              options={locationOptions}
            />
          )}

          <div
            className={`${styles.advanceFiltersButton} ${
              showAdvanced || activeFiltersCount > 0 ? styles.active : ""
            }`}
            onClick={onToggleFilters}
          >
            <SlidersHorizontal size={16} />
            <span>Advanced Filters</span>
            {activeFiltersCount > 0 && (
              <span className={styles.filterBadge}>{activeFiltersCount}</span>
            )}
          </div>
        </Flex>

        <Divider className={styles.divider} />

        {/* Row 3: Active Filter Chips & Search Button */}
        <div className={styles.filtersFooter}>
          <div className={styles.chipsArea}>
            <div className={styles.chipsRow}>
              {chips.map((chip) => (
                <span key={chip.key} className={styles.chip} title={chip.label}>
                  <span className={styles.chipLabel}>{chip.label}</span>
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => onRemoveFilter(chip.key)}
                    aria-label={`Remove ${chip.key} filter`}
                  >
                    ×
                  </button>
                </span>
              ))}
              {chips.length > 0 ? (
                <button
                  type="button"
                  className={styles.clearAllButton}
                  onClick={onClearFilters}
                >
                  Clear all
                </button>
              ) : null}
            </div>
          </div>
          <Button
            type="primary"
            className={styles.searchButton}
            onClick={onSearchClick}
          >
            Search
          </Button>
        </div>
      </div>
    </div>
  );
}
